import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useMagicReport } from '../context/MagicReportContext';
import { NotaryAndSignaturesSection } from './NotaryAndSignaturesSection';
import { ScheduleSection } from './ScheduleSection';
import { PlanCostReviewIssuesSection } from './PlanCostReviewIssuesSection';
import { ProgressDescriptionSection } from './ProgressDescriptionSection';
import { ProjectDescriptionSection } from './ProjectDescriptionSection';
import { MinorAdjustmentsSection } from './MinorAdjustmentsSection';
import { DocumentsReceivedSection } from './DocumentsReceivedSection';
import { MaterialTestingReportsSection } from './MaterialTestingReportsSection';
import { ConstructionMeetingMinutesSection } from './ConstructionMeetingMinutesSection';
import { PotentialChangeOrdersSection } from './PotentialChangeOrdersSection';
import { FoundationSurveySection } from './FoundationSurveySection';
import { RetainageSection } from './RetainageSection';
import { StoredMaterialsSection } from './StoredMaterialsSection';
import { ReallocationsSection } from './ReallocationsSection';
import { ChangeOrdersSection } from './ChangeOrdersSection';
import { LienWaiversSection } from './LienWaiversSection';
import { DynamicSectionsManager } from './DynamicSectionsManager';
import { DynamicSectionItem } from './DynamicSectionItem';
import { SectionFieldCollection } from '../magic-report.type';
import PreviousFundingsSection from './PreviousFundingsSection';
import { CoverLetterAmountsSection } from '../../shared/steps/budget/CoverLetterAmountsSection';
import { RecapOfPreviousReportsSection } from './RecapOfPreviousReportsSection';

/**
 * Main Sections Step component that contains all dynamic sections
 * Each section is rendered as a subsection with its own ID for navigation
 *
 * Architecture:
 * - Simple sections (Notary, Schedule, Progress) use MagicReportSection with registry configuration
 * - Complex sections (Minor Adjustments) use MagicReportSection with custom rendering
 * - All sections share common state management and field handling logic
 * - User-created dynamic sections are managed by DynamicSectionsManager and interleaved with static sections
 */
export interface SectionsStepProps {
  /** Ordered list of static section IDs to render in this step (Payapp Review vs Document Review). */
  staticSectionIds: readonly string[];
  /** Renders the "Amounts for Cover Letter" section at the top of this step (Payapp Review only). */
  showCoverLetterAmounts?: boolean;
  /** Renders the "Recap of Previous Reports" editor after every other section (Document Review only). */
  showRecapOfPreviousReports?: boolean;
}

export const SectionsStep: React.FC<SectionsStepProps> = ({ staticSectionIds, showCoverLetterAmounts, showRecapOfPreviousReports }) => {
  const { reportState, setReportState, setIsDirty } = useMagicReport();
  const magicReportId = reportState.reportId || reportState.magicReportId;

  if (!magicReportId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color='error'>Error: Report ID not found</Typography>
      </Box>
    );
  }

  const dynamicSections = (reportState.sections as any)?.dynamicSections || [];

  const isReviewingConstructionMeetingMinutes = reportState.projectSummary?.reviewingConstructionMeetingMinutes === true;
  const isReviewingPotentialChangeOrders = reportState.projectSummary?.reviewingPotentialChangeOrders === true;
  const visibleStaticSectionIds = useMemo(
    () =>
      staticSectionIds.filter(id => {
        if (id === 'construction-meeting-minutes') return isReviewingConstructionMeetingMinutes;
        if (id === 'potential-change-orders') return isReviewingPotentialChangeOrders;
        return true;
      }),
    [staticSectionIds, isReviewingConstructionMeetingMinutes, isReviewingPotentialChangeOrders],
  );

  // Build ordered list of all sections (static + dynamic interleaved)
  const orderedSections = useMemo(() => {
    const sections: Array<{ type: 'static' | 'dynamic'; id: string; data?: any; index?: number }> = [];

    // Group dynamic sections by their insertAfterSectionId
    const groupedDynamicSections = new Map<string, Array<{ section: any; originalIndex: number }>>();
    dynamicSections.forEach((section: SectionFieldCollection, index: number) => {
      const key = section.insertAfterSectionId || 'end';
      if (!groupedDynamicSections.has(key)) {
        groupedDynamicSections.set(key, []);
      }
      groupedDynamicSections.get(key)!.push({ section, originalIndex: index });
    });

    // Add dynamic sections at the start (those with insertAfterSectionId === 'start')
    const startDynamics = groupedDynamicSections.get('start') || [];
    startDynamics.forEach(({ section, originalIndex }) => {
      sections.push({
        type: 'dynamic',
        id: `dynamic-section-${originalIndex}`,
        data: section,
        index: originalIndex,
      });
    });

    // Iterate through static sections and insert dynamic sections after each
    visibleStaticSectionIds.forEach(staticId => {
      sections.push({ type: 'static', id: staticId });

      // Add all dynamic sections that come after this static section
      const dynamicsForThisPosition = groupedDynamicSections.get(staticId) || [];
      dynamicsForThisPosition.forEach(({ section, originalIndex }) => {
        sections.push({
          type: 'dynamic',
          id: `dynamic-section-${originalIndex}`,
          data: section,
          index: originalIndex,
        });
      });
    });

    // Add dynamic sections at the end (those with undefined or 'end' insertAfterSectionId)
    const endDynamics = groupedDynamicSections.get('end') || [];
    endDynamics.forEach(({ section, originalIndex }) => {
      sections.push({
        type: 'dynamic',
        id: `dynamic-section-${originalIndex}`,
        data: section,
        index: originalIndex,
      });
    });

    return sections;
  }, [dynamicSections, visibleStaticSectionIds]);

  // Handlers for dynamic sections
  const handleDeleteSection = (index: number) => {
    setReportState(prev => {
      const currentSections = (prev.sections as any) || {};
      const existingDynamicSections = currentSections.dynamicSections || [];
      const updatedDynamicSections = existingDynamicSections.filter((_: any, i: number) => i !== index);
      return {
        ...prev,
        sections: {
          ...currentSections,
          dynamicSections: updatedDynamicSections,
        } as any,
      };
    });
    setIsDirty(true);
  };

  const handleUpdateSection = (index: number, updatedData: SectionFieldCollection) => {
    setReportState(prev => {
      const currentSections = (prev.sections as any) || {};
      const existingDynamicSections = currentSections.dynamicSections || [];
      const updatedDynamicSections = existingDynamicSections.map((section: SectionFieldCollection, i: number) =>
        i === index ? { ...section, ...updatedData } : section,
      );
      return {
        ...prev,
        sections: {
          ...currentSections,
          dynamicSections: updatedDynamicSections,
        } as any,
      };
    });
    setIsDirty(true);
  };

  const getBoundarySlot = (insertAfterSectionId: string | undefined): number => {
    const key = insertAfterSectionId || 'end';
    if (key === 'start') return -1;
    if (key === 'end') return visibleStaticSectionIds.length - 1;
    const idx = visibleStaticSectionIds.findIndex(id => id === key);
    return idx === -1 ? visibleStaticSectionIds.length - 1 : idx;
  };

  const boundarySlotToInsertAfterSectionId = (slot: number): string | undefined => {
    if (slot < 0) return 'start';
    if (slot >= visibleStaticSectionIds.length - 1) return undefined; // undefined === 'end'
    return visibleStaticSectionIds[slot];
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    setReportState(prev => {
      const currentSections = (prev.sections as any) || {};
      const existingDynamicSections = [...(currentSections.dynamicSections || [])];
      const section = existingDynamicSections[index] as any;
      const currentInsertAfter = section?.insertAfterSectionId || 'end';

      // Find all sections with the same insertAfterSectionId (in the same group)
      const sameGroupIndices: number[] = [];
      existingDynamicSections.forEach((s: any, i: number) => {
        const sInsertAfter = s?.insertAfterSectionId || 'end';
        if (sInsertAfter === currentInsertAfter) {
          sameGroupIndices.push(i);
        }
      });

      // Find position within the group
      const positionInGroup = sameGroupIndices.indexOf(index);

      // Check if we're moving within the same group or across boundaries
      if (direction === 'up' && positionInGroup > 0) {
        // Move up within the same group - swap with previous in group
        const swapWithIndex = sameGroupIndices[positionInGroup - 1];
        [existingDynamicSections[index], existingDynamicSections[swapWithIndex]] = [
          existingDynamicSections[swapWithIndex],
          existingDynamicSections[index],
        ];
      } else if (direction === 'down' && positionInGroup < sameGroupIndices.length - 1) {
        // Move down within the same group - swap with next in group
        const swapWithIndex = sameGroupIndices[positionInGroup + 1];
        [existingDynamicSections[index], existingDynamicSections[swapWithIndex]] = [
          existingDynamicSections[swapWithIndex],
          existingDynamicSections[index],
        ];
      } else {
        // Moving across a static section boundary — step exactly one slot at a time.
        const currentSlot = getBoundarySlot(currentInsertAfter);
        const newSlot = direction === 'up' ? currentSlot - 1 : currentSlot + 1;

        if (newSlot < -1 || newSlot > visibleStaticSectionIds.length - 1) {
          return prev; // Can't move past 'start' or past 'end'
        }

        existingDynamicSections[index] = { ...section, insertAfterSectionId: boundarySlotToInsertAfterSectionId(newSlot) };
      }

      return {
        ...prev,
        sections: {
          ...currentSections,
          dynamicSections: existingDynamicSections,
        } as any,
      };
    });
    setIsDirty(true);

    // Scroll to the section after state update (for both up and down moves)
    setTimeout(() => {
      const sectionElement = document.getElementById(`dynamic-section-${index}`);
      if (sectionElement) {
        // Use scrollIntoView which works with any scroll container
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150); // Small delay to ensure React has re-rendered with new position
  };

  // Helper to get canMoveUp/canMoveDown for a dynamic section
  const getMoveCapabilities = (section: SectionFieldCollection, index: number) => {
    const currentInsertAfter = (section as any)?.insertAfterSectionId || 'end';

    // Find all sections in the same group
    const sameGroupIndices: number[] = [];
    dynamicSections.forEach((s: any, i: number) => {
      const sInsertAfter = s?.insertAfterSectionId || 'end';
      if (sInsertAfter === currentInsertAfter) {
        sameGroupIndices.push(i);
      }
    });

    const positionInGroup = sameGroupIndices.indexOf(index);
    const currentSlot = getBoundarySlot(currentInsertAfter);

    // Can move up if: not first in group OR the boundary itself can move to a lower slot
    const canMoveUp = positionInGroup > 0 || currentSlot > -1;

    // Can move down if: not last in group OR the boundary itself can move to a higher slot
    const canMoveDown = positionInGroup < sameGroupIndices.length - 1 || currentSlot < visibleStaticSectionIds.length - 1;

    return { canMoveUp, canMoveDown };
  };

  return (
    <Box>
      {showCoverLetterAmounts && <CoverLetterAmountsSection />}

      {/* Render all sections in order */}
      {orderedSections.map(section => {
        if (section.type === 'static') {
          // Render static section
          switch (section.id) {
            case 'previous-fundings':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <PreviousFundingsSection />
                </Box>
              );
            case 'notary-and-signatures':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <NotaryAndSignaturesSection magicReportId={magicReportId} />
                </Box>
              );
            case 'schedule':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <ScheduleSection magicReportId={magicReportId} />
                </Box>
              );
            case 'stored-materials':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <StoredMaterialsSection magicReportId={magicReportId} />
                </Box>
              );
            case 'plan-cost-review-issues':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <PlanCostReviewIssuesSection magicReportId={magicReportId} />
                </Box>
              );
            case 'foundation-survey':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <FoundationSurveySection magicReportId={magicReportId} />
                </Box>
              );
            case 'progress-description':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <ProgressDescriptionSection magicReportId={magicReportId} />
                </Box>
              );
            case 'minor-adjustments':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <MinorAdjustmentsSection magicReportId={magicReportId} />
                </Box>
              );
            case 'docs-received':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <DocumentsReceivedSection magicReportId={magicReportId} />
                </Box>
              );
            case 'material-testing-reports':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <MaterialTestingReportsSection magicReportId={magicReportId} />
                </Box>
              );
            case 'construction-meeting-minutes':
              return isReviewingConstructionMeetingMinutes ? (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <ConstructionMeetingMinutesSection magicReportId={magicReportId} />
                </Box>
              ) : null;
            case 'retainage':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <RetainageSection magicReportId={magicReportId} />
                </Box>
              );
            case 'reallocations':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <ReallocationsSection magicReportId={magicReportId} />
                </Box>
              );
            case 'change-orders':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <ChangeOrdersSection magicReportId={magicReportId} />
                </Box>
              );
            case 'potential-change-orders':
              return isReviewingPotentialChangeOrders ? (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <PotentialChangeOrdersSection magicReportId={magicReportId} />
                </Box>
              ) : null;
            case 'lien-waivers':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <LienWaiversSection magicReportId={magicReportId} />
                </Box>
              );
            case 'project-description':
              return (
                <Box key={section.id} id={section.id} sx={{ mb: 4 }}>
                  <ProjectDescriptionSection magicReportId={magicReportId} />
                </Box>
              );
            default:
              return null;
          }
        } else {
          // Render dynamic section
          const { canMoveUp, canMoveDown } = getMoveCapabilities(section.data, section.index!);
          return (
            <DynamicSectionItem
              key={section.id}
              sectionData={section.data}
              sectionIndex={section.index!}
              onDelete={() => handleDeleteSection(section.index!)}
              onUpdate={updatedData => handleUpdateSection(section.index!, updatedData)}
              onMoveUp={() => handleMoveSection(section.index!, 'up')}
              onMoveDown={() => handleMoveSection(section.index!, 'down')}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
            />
          );
        }
      })}

      {showRecapOfPreviousReports && <RecapOfPreviousReportsSection />}

      {/* Dynamic Sections Manager (for the FAB button) */}
      <DynamicSectionsManager />
    </Box>
  );
};
